/**
 * LooksCacheService — Cache mémoire + persistant des générations de looks
 *
 * Objectif : éviter de recalculer la même génération de looks
 * si les inputs (wardrobe, catalog, météo, occasion) n'ont pas changé.
 *
 * 2 niveaux de cache :
 *  1. Mémoire (Map) : accès instantané durant la session
 *  2. AsyncStorage : survit au redémarrage de l'app
 *
 * Stratégie d'invalidation :
 *  - TTL 5 min en mémoire
 *  - TTL 24h persistant
 *  - Signature basée sur wardrobe + catalog count + temp + occasion
 *  - Si un de ces change → nouvelle clé → cache miss → regénération
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const CACHE_TTL_MS       = 5 * 60 * 1000;        // 5 min mémoire
const PERSIST_TTL_MS     = 24 * 60 * 60 * 1000;  // 24h persistant
const MAX_ENTRIES        = 20;                   // LRU cap en mémoire
const PERSIST_PREFIX     = 'looks_cache_v2_';

// Sur web, AsyncStorage est polyfillé par localStorage (quota ~5MB).
// Les payloads de looks (catalogue × wardrobe) sont gros : on plafonne
// à 1.5 Mo par entrée et on purge agressivement quand le quota est atteint.
const WEB_MAX_ENTRY_BYTES   = 1_500_000;  // 1.5 MB / entrée
const WEB_MAX_PERSIST_KEYS  = 6;          // Cap nombre d'entrées looks_cache sur web

function isQuotaError(e) {
  if (!e) return false;
  // Browsers : DOMException name 'QuotaExceededError' ou code 22 (Chrome) / 1014 (Firefox)
  return /quota|exceeded/i.test(e.name || '')
      || /quota|exceeded/i.test(e.message || '')
      || e.code === 22 || e.code === 1014;
}

const cache = new Map(); // key → { data, ts }

/**
 * Construit une clé de cache stable à partir des inputs.
 * Inclut :
 *  - Nombre d'articles wardrobe + hash des 10 premiers ids (triés)
 *  - Nombre de produits catalog + hash des 3 premiers ids (triés)
 *  - Température arrondie à 3°C
 *  - Occasion
 */
export function makeKey(namespace, wardrobe, catalog, temp, occasion, maxLooks) {
  const wLen = Array.isArray(wardrobe) ? wardrobe.length : 0;
  const cLen = Array.isArray(catalog) ? catalog.length : 0;

  const wSig = Array.isArray(wardrobe)
    ? wardrobe.slice(0, 10).map(i => i?.id || '').sort().join(',')
    : '';

  const cSig = Array.isArray(catalog)
    ? catalog.slice(0, 3).map(p => p?.id || '').sort().join(',')
    : '';

  const tBucket = Math.round((Number(temp) || 20) / 3) * 3;

  // v32 (2026-05-03) : QUALITY_THRESHOLD 38 → 50 (afficher score ≥ 50%).
  // v33 (2026-05-15) : invalidation forcée après alignement strict.
  // v34 (2026-05-15) : nouvelle invalidation après bascule Looks AI sur worker
  // CDK direct (forceLocalCompute) + HERO top-2. Évite que localStorage v33
  // contenant un ancien batch outfit-api (128 outfits) écrase le nouveau résultat.
  return `${namespace}|v34|${wLen}|${wSig}|${cLen}|${cSig}|${tBucket}|${occasion || 'Tout'}|${maxLooks || 5}`;
}

export function getCache(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

export function setCache(key, data) {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, { data, ts: Date.now() });
  while (cache.size > MAX_ENTRIES) {
    const firstKey = cache.keys().next().value;
    cache.delete(firstKey);
  }
}

/**
 * Récupère depuis mémoire, puis AsyncStorage en fallback.
 * Usage : au démarrage de LooksScreen pour un rendu instantané.
 */
export async function getCachePersisted(key) {
  const memHit = getCache(key);
  if (memHit) return memHit;

  try {
    const raw = await AsyncStorage.getItem(PERSIST_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data) return null;
    if (Date.now() - parsed.ts > PERSIST_TTL_MS) {
      await AsyncStorage.removeItem(PERSIST_PREFIX + key);
      return null;
    }
    setCache(key, parsed.data);
    return parsed.data;
  } catch (e) {
    return null;
  }
}

/**
 * Stocke en mémoire + AsyncStorage pour survie au redémarrage.
 *
 * Sur web : localStorage a un quota ~5 MB. Pour éviter "QuotaExceededError",
 * on saute l'entrée si elle est trop grosse, on plafonne le nombre de clés
 * looks_cache_*, et on purge les anciennes entrées quand le quota est atteint.
 */
export async function setCachePersisted(key, data) {
  setCache(key, data);
  const isWeb = Platform.OS === 'web';
  const fullKey = PERSIST_PREFIX + key;
  const payload = JSON.stringify({ ts: Date.now(), data });

  // Web — skip si payload trop volumineux (évite quota dès l'écriture)
  if (isWeb && payload.length > WEB_MAX_ENTRY_BYTES) {
    return; // mémoire seulement, pas de persistance
  }

  // Web — cap proactif sur le nombre d'entrées looks_cache_* (LRU côté disque)
  if (isWeb) {
    try {
      const allKeys = await AsyncStorage.getAllKeys();
      const looksKeys = allKeys.filter(k => k.startsWith(PERSIST_PREFIX));
      if (looksKeys.length >= WEB_MAX_PERSIST_KEYS) {
        // Purger toutes les entrées (simple et efficace : on garde mémoire)
        await AsyncStorage.multiRemove(looksKeys);
      }
    } catch {}
  }

  try {
    await AsyncStorage.setItem(fullKey, payload);
  } catch (e) {
    if (isQuotaError(e)) {
      // Quota dépassé : purge complète des entrées looks_cache_* et retry une fois
      try {
        const allKeys = await AsyncStorage.getAllKeys();
        const toRemove = allKeys.filter(k => k.startsWith(PERSIST_PREFIX));
        if (toRemove.length > 0) await AsyncStorage.multiRemove(toRemove);
        await AsyncStorage.setItem(fullKey, payload);
        return;
      } catch {
        // Échec silencieux : la mémoire suffit pour la session courante
        return;
      }
    }
    console.warn('[LooksCache] persist failed:', e.message);
  }
}

/**
 * Invalide toutes les entrées d'un namespace donné (mémoire + AsyncStorage).
 */
export async function invalidateNamespace(namespace) {
  const prefix = namespace + '|';
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
  try {
    const allKeys = await AsyncStorage.getAllKeys();
    const toRemove = allKeys.filter(k =>
      k.startsWith(PERSIST_PREFIX) && k.slice(PERSIST_PREFIX.length).startsWith(prefix)
    );
    if (toRemove.length > 0) {
      await AsyncStorage.multiRemove(toRemove);
      console.log(`[LooksCache] invalidated ${toRemove.length} persisted entries for ns=${namespace}`);
    }
  } catch {}
}

export async function clearCache() {
  cache.clear();
  try {
    const allKeys = await AsyncStorage.getAllKeys();
    const toRemove = allKeys.filter(k => k.startsWith(PERSIST_PREFIX));
    if (toRemove.length > 0) await AsyncStorage.multiRemove(toRemove);
  } catch {}
}

export function getCacheStats() {
  return {
    size: cache.size,
    keys: Array.from(cache.keys()).map(k => k.slice(0, 80)),
  };
}
